/**
 * Composable for handling referral code attribution
 *
 * Captures referral codes from URL parameters and stores them in cookies.
 * Codes are sent with login requests to attribute new users to referrers.
 * A Speed Wallet mini-app arrival (`?acct=`) is credited to the Speed Wallet
 * affiliate account: `useSpeedWallet` fetches its code (system_config, admin
 * editable) and stores it through `storeReferralCode` exactly like a `?ref=`.
 *
 * Cookies are scoped to the registrable apex domain (e.g. `.aurumrun.com`) so
 * that a referral captured on one subdomain (the deployed app, a landing page,
 * etc.) is also visible on the root domain and every other subdomain.
 */

import { parseSpeedWalletParams } from '~/utils/speedWallet'

const COOKIE_MAX_AGE = 30 * 24 * 60 * 60 // 30 days

/**
 * Resolve the current request's hostname + scheme, isomorphically.
 *
 * `useRequestURL` is a Nuxt auto-import resolved on both server and client;
 * the `typeof` guard covers environments where it is absent (unit tests),
 * falling back to `window.location`. Returns `undefined` only outside a
 * request/browser context.
 */
function resolveHost(): { hostname: string; secure: boolean } | undefined {
  if (typeof useRequestURL === 'function') {
    try {
      const url = useRequestURL()
      return { hostname: url.hostname, secure: url.protocol === 'https:' }
    } catch {
      // fall through to window-based resolution
    }
  }
  if (typeof window !== 'undefined') {
    return {
      hostname: window.location.hostname,
      secure: window.location.protocol === 'https:',
    }
  }
  return undefined
}

/**
 * Compute the cookie `domain` + `secure` for the current host.
 *
 * `domain` is the apex prefixed with a leading dot (`.aurumrun.com`) so the
 * cookie is shared across the root domain and all subdomains. It is omitted
 * for `localhost` / IPv4 / IPv6 / single-label hosts, where browsers reject
 * the `Domain` attribute and a host-only cookie is the only valid form.
 *
 * `secure` tracks the request scheme (https) rather than domain presence, so
 * a domain-scoped cookie can still be set over plain HTTP for local
 * subdomain testing (e.g. `http://app.lvh.me:3000`).
 *
 * Assumes a single-segment TLD (e.g. `.com`), which matches this project's
 * domain. Multi-segment public suffixes (e.g. `.co.uk`) are not handled.
 */
function getCookieScope(): { domain?: string; secure: boolean } {
  const host = resolveHost()
  if (!host) return { secure: false }

  // Normalize a trailing-dot FQDN ("aurumrun.com." → "aurumrun.com") so the
  // apex math below cannot produce an invalid ".com." domain.
  const hostname = host.hostname.replace(/\.$/, '')

  // localhost / IPv4 / IPv6 literal → host-only cookie (Domain rejected).
  if (
    hostname === 'localhost' ||
    hostname.includes(':') ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
  ) {
    return { secure: host.secure }
  }

  const parts = hostname.split('.')
  if (parts.length < 2) return { secure: host.secure }

  return { domain: `.${parts.slice(-2).join('.')}`, secure: host.secure }
}

/**
 * Normalize a `route.query` value to a plain string or null.
 *
 * `route.query.*` is `string | string[] | undefined` — a duplicated
 * `?utm_source=a&utm_source=b` (or a network redirect adding a second param)
 * yields an array, and an `as string` cast is a compile-time lie that let the
 * array reach the cookie and the login payload, crashing the backend's
 * `.trim()`. Take the first value so what we store is always a plain non-empty
 * string or null.
 */
export function firstQuery(v: unknown): string | null {
  const s = Array.isArray(v) ? v[0] : v
  return typeof s === 'string' && s.length > 0 ? s : null
}

/** Acquisition signals for a single visit, already normalized via {@link firstQuery}. */
export interface AttributionSignals {
  ref: string | null
  /** The visit came from the Speed Wallet mini app (`?acct=`). Counts as a
   *  referral touch even though its code arrives asynchronously. */
  speedWalletArrival?: boolean
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmTerm: string | null
  utmContent: string | null
  /** Extra ad-network click slots (?param1..3) — payload carried with the utm
   *  bundle, never a signal of their own (see `hasNewUtm` below). */
  param1: string | null
  param2: string | null
  param3: string | null
  landingPage: string | null
  httpReferrer: string | null
}

/** The stored `attribution` cookie shape (snake_case, as sent to the backend). */
export interface StoredAttribution {
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_term: string | null
  utm_content: string | null
  param1: string | null
  param2: string | null
  param3: string | null
  landing_page: string | null
  http_referrer: string | null
}

export interface AttributionOutcome {
  /** Overwrite the `attribution` cookie with `attribution`? When false, keep the stored value. */
  write: boolean
  attribution: StoredAttribution | null
  /** Also clear `ref_code` (+ `ref_utm`): a new marketing utm without a ref supersedes an earlier referral. */
  clearRef: boolean
}

/**
 * Last-touch, ref-aware attribution decision (variant A).
 *
 * Keeps ONE "last-touch" source — whichever arrives later, an affiliate `?ref=`
 * or a marketing `utm`, wins and clears the other:
 *  - new `?ref=`      → new affiliate: overwrite utm with THIS visit's utm
 *                       (empty if none this visit). `ref_code` is kept — it was
 *                       just set by `captureReferralCode`.
 *  - new utm, no ref  → ad/marketing last touch: overwrite utm AND clear the ref,
 *                       because the earlier referral is no longer what brought
 *                       the user back (variant A — ANY new utm counts).
 *  - no ref, no utm   → organic/direct return: keep the stored source. When
 *                       nothing is stored yet, seed a direct landing once so a
 *                       returning session doesn't re-evaluate every navigation.
 *
 * Pure and deterministic — the composable's `captureAttribution` is a thin
 * wrapper that feeds it normalized query values and applies the outcome. (It is
 * split out because `captureAttribution` is client-gated and can't run in unit
 * tests; this function is where the logic lives and is fully tested.)
 */
export function decideAttribution(
  s: AttributionSignals,
  hasStoredAttribution: boolean,
): AttributionOutcome {
  // param1..3 are deliberately NOT part of this test. They are payload the ad
  // network wants echoed back, not a marketing touch: letting a bare `?param1=`
  // count as one would overwrite a stored source and (below) drop the affiliate
  // ref, so any link could silently break attribution. They ride along with
  // whatever touch the branches below decide to write.
  const hasNewUtm = !!(s.utmSource || s.utmMedium || s.utmCampaign || s.utmTerm || s.utmContent)
  // A Speed Wallet arrival is a referral touch too: its code is fetched after
  // this decision runs, and a same-visit utm must not wipe it (variant A).
  const hasNewRef = s.ref !== null || s.speedWalletArrival === true

  // Organic/direct return with something already stored → don't clobber it.
  if (!hasNewRef && !hasNewUtm && hasStoredAttribution) {
    return { write: false, attribution: null, clearRef: false }
  }

  return {
    write: true,
    attribution: {
      utm_source: s.utmSource,
      utm_medium: s.utmMedium,
      utm_campaign: s.utmCampaign,
      utm_term: s.utmTerm,
      utm_content: s.utmContent,
      param1: s.param1,
      param2: s.param2,
      param3: s.param3,
      landing_page: s.landingPage,
      http_referrer: s.httpReferrer,
    },
    // Variant A: a new marketing utm WITHOUT a ref supersedes an earlier referral.
    clearRef: !hasNewRef && hasNewUtm,
  }
}

export function useReferral() {
  const route = useRoute()

  // Shared options so that the cookie set during capture can also be deleted
  // during clear — browsers require matching Domain + Path to expire a cookie.
  const { domain: cookieDomain, secure } = getCookieScope()
  const baseCookieOptions = {
    path: '/',
    sameSite: 'lax' as const,
    secure,
    ...(cookieDomain ? { domain: cookieDomain } : {}),
  }

  /**
   * Store a referral code (30-day cookie) plus this visit's UTM for analytics.
   *
   * Shared by the `?ref=` capture below and by `useSpeedWallet`, whose code is
   * resolved asynchronously from the backend config — both must write exactly
   * the same cookies so the login payload cannot tell the two apart.
   */
  function storeReferralCode(refCode: string): void {
    const cookie = useCookie('ref_code', {
      ...baseCookieOptions,
      maxAge: COOKIE_MAX_AGE,
    })
    cookie.value = refCode

    const utmCookie = useCookie('ref_utm', {
      ...baseCookieOptions,
      maxAge: COOKIE_MAX_AGE,
    })
    utmCookie.value = JSON.stringify({
      source: firstQuery(route.query.utm_source),
      medium: firstQuery(route.query.utm_medium),
      campaign: firstQuery(route.query.utm_campaign),
      content: firstQuery(route.query.utm_content),
    })
  }

  /**
   * Capture referral code from URL query parameters
   * Stores code in a 30-day cookie for attribution
   */
  function captureReferralCode(): string | null {
    const refCode = firstQuery(route.query.ref)
    if (!refCode) return null
    storeReferralCode(refCode)
    return refCode
  }

  /**
   * Record an affiliate CLICK when the app is opened with a `?ref=` param.
   *
   * The real affiliate share link (`https://.../?ref=CODE`) lands directly on
   * the app rather than the redirect `affiliates-track` endpoint, so the click
   * is recorded here: a fire-and-forget POST to the public `affiliates-click`
   * function, which derives the client IP server-side, dedups by ip_hash within
   * 24h, and stores the click + UTM. Client-only and non-blocking — a failure
   * must never affect the landing experience.
   *
   * `code` overrides the `?ref=` lookup for a referral resolved elsewhere (the
   * Speed Wallet affiliate code, see useSpeedWallet).
   */
  function recordClick(code?: string): void {
    if (!import.meta.client) return

    const refCode = code ?? firstQuery(route.query.ref)
    if (!refCode) return

    const config = useRuntimeConfig()
    const apiBase = config.public.apiBase as string | undefined
    if (!apiBase) return

    // Fire-and-forget; swallow every error.
    $fetch(`${apiBase}/affiliates-click`, {
      method: 'POST',
      body: {
        ref: refCode,
        utm_source: firstQuery(route.query.utm_source) ?? undefined,
        utm_medium: firstQuery(route.query.utm_medium) ?? undefined,
        utm_campaign: firstQuery(route.query.utm_campaign) ?? undefined,
        utm_content: firstQuery(route.query.utm_content) ?? undefined,
      },
    }).catch(() => {})
  }

  /**
   * Capture acquisition attribution for ALL visitors — independent of `?ref=`.
   *
   * Last-touch, ref-aware (see {@link decideAttribution}): the `attribution`
   * cookie follows the most recent marketing touch instead of being frozen on
   * the first visit. A new `?ref=` or a new `utm` overwrites it; an organic
   * return preserves it. A new ad/utm touch WITHOUT a ref also drops the stored
   * affiliate ref (variant A), so a referral is credited only when the affiliate
   * actually brought the user in on the same touch.
   *
   * Client-only: `document.referrer` is unavailable during SSR, and the app is
   * an SPA so client JS always runs.
   */
  function captureAttribution(): void {
    if (!import.meta.client) return

    const stored = useCookie('attribution')
    const outcome = decideAttribution(
      {
        ref: firstQuery(route.query.ref),
        speedWalletArrival: parseSpeedWalletParams(route.query) !== null,
        utmSource: firstQuery(route.query.utm_source),
        utmMedium: firstQuery(route.query.utm_medium),
        utmCampaign: firstQuery(route.query.utm_campaign),
        utmTerm: firstQuery(route.query.utm_term),
        utmContent: firstQuery(route.query.utm_content),
        param1: firstQuery(route.query.param1),
        param2: firstQuery(route.query.param2),
        param3: firstQuery(route.query.param3),
        landingPage: route.path || null,
        httpReferrer: document.referrer || null,
      },
      !!stored.value,
    )

    if (!outcome.write) return

    const cookie = useCookie('attribution', {
      ...baseCookieOptions,
      maxAge: COOKIE_MAX_AGE,
    })
    cookie.value = JSON.stringify(outcome.attribution)

    // Variant A: an ad/marketing touch that lands after an earlier referral is
    // now the last touch — drop the affiliate ref so it isn't credited.
    if (outcome.clearRef) clearReferralCode()
  }

  /**
   * Get stored acquisition attribution from cookie (for the login request).
   */
  function getAttribution(): {
    utm_source: string | null
    utm_medium: string | null
    utm_campaign: string | null
    utm_term: string | null
    utm_content: string | null
    param1: string | null
    param2: string | null
    param3: string | null
    landing_page: string | null
    http_referrer: string | null
  } | null {
    const cookie = useCookie('attribution')
    if (!cookie.value) return null
    try {
      return typeof cookie.value === 'string'
        ? JSON.parse(cookie.value)
        : (cookie.value as Record<string, string | null>)
    } catch {
      return null
    }
  }

  /**
   * Get stored referral code from cookie.
   *
   * Normalizes legacy cookie shapes: the pre-fix code could store an array
   * (duplicated `?ref=a&ref=b`), and Nuxt's destr decode turns a digit-only
   * code into a number — always hand the backend a plain string or null.
   */
  function getReferralCode(): string | null {
    const raw = useCookie('ref_code').value as unknown
    const v = Array.isArray(raw) ? raw[0] : raw
    if (typeof v === 'string' && v.length > 0) return v
    if (typeof v === 'number') return String(v)
    return null
  }

  /**
   * Get stored UTM parameters from cookie
   */
  function getUtmParams(): { source: string | null; medium: string | null; campaign: string | null; content: string | null } | null {
    const cookie = useCookie('ref_utm')
    if (!cookie.value) return null

    try {
      // Nuxt's useCookie destr-decodes JSON cookies into objects already;
      // JSON.parse is only needed for the raw-string case (unit tests, other
      // writers). Mirrors getAttribution's handling.
      return typeof cookie.value === 'string'
        ? JSON.parse(cookie.value)
        : (cookie.value as { source: string | null; medium: string | null; campaign: string | null; content: string | null })
    } catch {
      return null
    }
  }

  /**
   * Clear referral code after successful attribution.
   * Must use the same domain/path as capture so the shared cookie is removed.
   */
  function clearReferralCode(): void {
    const cookie = useCookie('ref_code', baseCookieOptions)
    cookie.value = null

    const utmCookie = useCookie('ref_utm', baseCookieOptions)
    utmCookie.value = null
  }

  /**
   * Clear the acquisition attribution cookie after it has been consumed by a
   * successful signup/login. Without this a SECOND account created from the
   * same browser would replay the first account's utm_source/utm_content and
   * enqueue a duplicate ad-network lead for the same click id (the outbox
   * UNIQUE(user_id, event_type) only dedups per user). Kept separate from
   * clearReferralCode: captureAttribution's variant-A path clears the ref
   * right after WRITING a fresh attribution — that write must survive.
   */
  function clearAttribution(): void {
    const cookie = useCookie('attribution', baseCookieOptions)
    cookie.value = null
  }

  return {
    captureReferralCode,
    storeReferralCode,
    captureAttribution,
    recordClick,
    getReferralCode,
    getUtmParams,
    getAttribution,
    clearReferralCode,
    clearAttribution,
  }
}
